import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      if (u?.must_change_password) {
        navigate("/force-change-password");
      } else {
        navigate("/");
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = formatErr(err?.response?.data?.detail);
      if (status === 423) {
        toast.error(detail, { duration: 8000 });
      } else {
        toast.error(detail || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left visual side */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden bg-[#0F2E4A]">
        <img
          src="https://images.pexels.com/photos/21415155/pexels-photo-21415155.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
          alt="Campus"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F2E4A] via-[#0F2E4A]/80 to-[#0F2E4A]/40" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-lg bg-white grid place-items-center p-1.5">
              <img src="https://customer-assets.emergentagent.com/job_excel-learn-hub-2/artifacts/i2tti92b_image.png" alt="Quiddity" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-heading font-bold text-xl leading-none">Quiddity</div>
              <div className="text-[11px] uppercase tracking-[0.2em] mt-1 text-white/70">Institute of Excellence</div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">— Est. Excellence in Learning</div>
            <h1 className="font-heading text-5xl font-bold leading-[1.05]">
              Build your future.<br />
              <span className="text-[#34D399]">One discipline</span> at a time.
            </h1>
            <p className="text-white/70 text-sm leading-relaxed max-w-md">
              Track attendance, submit assignments, attend assessments and unlock placement opportunities — all from one cohesive portal.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4 text-xs">
            <div><div className="font-heading text-2xl font-bold">500+</div><div className="text-white/60 uppercase tracking-widest text-[10px] mt-1">Students</div></div>
            <div><div className="font-heading text-2xl font-bold">94%</div><div className="text-white/60 uppercase tracking-widest text-[10px] mt-1">Placement</div></div>
            <div><div className="font-heading text-2xl font-bold">12</div><div className="text-white/60 uppercase tracking-widest text-[10px] mt-1">Programs</div></div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 grid place-items-center px-6 py-12 bg-grain">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_excel-learn-hub-2/artifacts/i2tti92b_image.png" alt="Quiddity" className="w-10 h-10 object-contain" />
            <div className="font-heading font-bold text-xl">Quiddity</div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">Sign in to your portal</div>
          <h2 className="font-heading text-3xl font-bold text-[#0F172A] mb-2">Welcome back</h2>
          <p className="text-sm text-[#475569] mb-8">Enter your credentials to continue your learning journey.</p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[#475569] font-semibold">Email Address</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                placeholder="you@quiddity.edu"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-[#475569] font-semibold">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                required
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 bg-[#0F2E4A] hover:bg-[#174169] text-white"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
